/**
 * @file index.tsx
 *
 * @fileoverview Renders landing page job teasers.
 */
import { FormattedMessage } from 'react-intl';
import BookmarkedJobsProvider from '~/jobiqo/components/BookmarkedJobs/context';
import JobTeaserCard from '~/jobiqo/components/Job/Teaser';
import { LpJobTeasersParagraph } from '~/jobiqo/types/types';
import { Job } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';

interface JobTeasersProps {
  /**
   * The job teasers row.
   */
  row: LpJobTeasersParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export default function JobTeasers({ row, maxContainerSize }: JobTeasersProps) {
  const jobs = (row.jobs ?? []) as Job[];

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 12}
      paddingBottom={row.spacing ?? 12}
      {...(row.background && {
        backgroundColorOverride: `var(--color-${row.background})`,
      })}
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <FlexItems justifyContent="space-between" alignItems="center">
          <FlexItem>
            {row.jobTeasersSubTitle && (
              <>
                <Text
                  color="primary"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  {row.jobTeasersSubTitle}
                </Text>
                <Spacing size="medium" />
              </>
            )}
            {row.jobTeasersTitle && (
              <>
                <Text tag="h2" fontSize={[5, 6]} lineHeight={[5, 6]}>
                  {row.jobTeasersTitle}
                </Text>
                <Spacing size="large" />
              </>
            )}
          </FlexItem>
        </FlexItems>
        {jobs.length > 0 && (
          <>
            <Grid columns={[1, 2]} margin={0}>
              <BookmarkedJobsProvider
                initialState={jobs.filter((job) => job.bookmarked)}
              >
                {jobs.map((job) => (
                  <Cell display="grid" key={job.id}>
                    <JobTeaserCard job={job} />
                  </Cell>
                ))}
              </BookmarkedJobsProvider>
            </Grid>
            {row.url?.path && (
              <>
                <Spacing size="medium" />
                <Link href={row.url.path} prefetch={false}>
                  <FormattedMessage
                    id="similar_jobs"
                    defaultMessage="Similar jobs"
                  />
                </Link>
              </>
            )}
          </>
        )}
      </Main>
    </Box>
  );
}
export { JobTeasers };
